/**
 * VendorPortal — single-file vendor account experience.
 *
 * Routes:
 *   /vendor               → redirects to /vendor/login or /vendor/dashboard
 *   /vendor/login         → signup + login tabs
 *   /vendor/dashboard     → authenticated inbox + profile editor + reviews
 *
 * Session token is stored in localStorage under `crc_vendor_token` so it
 * never collides with the landlord/tenant session key (`crc_session_token`).
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import {
  Loader2, LogIn, UserPlus, Inbox, CheckCircle2, XCircle, Star,
  Mail, Phone, MapPin, Edit3, RefreshCw, LogOut, Building2, AlertCircle,
  Rocket, Crown,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const TOKEN_KEY = "crc_vendor_token";
const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT","ALL"];
const CATEGORIES = ["Plumbing","Electrical","HVAC","Cleaning","Painting","Locksmith","Roofing","Landscaping","Pest control","Snow removal","Handyman","Appliance repair","Flooring","General contractor","Other"];

function getToken() { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } }
function setToken(t) { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } }

async function vendorApi(path, { method = "GET", body, token } = {}) {
  const headers = { "Content-Type": "application/json" };
  const tk = token ?? getToken();
  if (tk) headers["x-vendor-token"] = tk;
  const r = await fetch(`${API}/api${path}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
  return d;
}

// ───────────────────── LOGIN / SIGNUP ────────────────────────────
function LoginSignup({ onAuthed }) {
  const [mode, setMode] = useState("login");
  return (
    <div className="bg-white min-h-screen" data-testid="vendor-auth-page">
      <PublicHeader light={true}/>
      <main className="max-w-md mx-auto px-5 md:px-8 py-10">
        <div className="text-center mb-6">
          <Building2 className="w-10 h-10 text-emerald-700 mx-auto mb-2"/>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tighter">Vendor portal</h1>
          <p className="text-sm text-slate-600 mt-1">Service providers — manage your Central Rentals listing + see new bookings.</p>
        </div>
        <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1 mb-6" data-testid="vendor-auth-tabs">
          <button onClick={() => setMode("login")} data-testid="vendor-tab-login"
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${mode==="login"?"bg-white shadow-sm text-slate-900":"text-slate-500"}`}>Sign in</button>
          <button onClick={() => setMode("signup")} data-testid="vendor-tab-signup"
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${mode==="signup"?"bg-white shadow-sm text-slate-900":"text-slate-500"}`}>Create account</button>
        </div>
        {mode === "login" ? <LoginForm onAuthed={onAuthed}/> : <SignupForm onAuthed={onAuthed}/>}
      </main>
    </div>
  );
}

function LoginForm({ onAuthed }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit(e) {
    e?.preventDefault?.();
    setErr(""); setBusy(true);
    try {
      const d = await vendorApi("/vendor-auth/login", { method: "POST", body: form });
      setToken(d.session_token); onAuthed(d);
      toast.success(`Welcome back, ${d.account?.business_name || "vendor"}!`);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="vendor-login-form">
      <Input testid="vendor-login-email" label="Email" type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})}/>
      <Input testid="vendor-login-password" label="Password" type="password" required value={form.password} onChange={(v) => setForm({...form, password: v})}/>
      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="vendor-login-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="vendor-login-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <LogIn className="w-4 h-4"/>} Sign in
      </button>
    </form>
  );
}

function SignupForm({ onAuthed }) {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", password: "",
    phone: "", category: CATEGORIES[0], provinces: ["ALL"], description: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit(e) {
    e?.preventDefault?.();
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setErr(""); setBusy(true);
    try {
      const d = await vendorApi("/vendor-auth/signup", { method: "POST", body: form });
      setToken(d.session_token); onAuthed(d);
      toast.success("Account created — your listing is in review (usually approved within 1 business day).");
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="vendor-signup-form">
      <Input testid="vendor-signup-business" label="Business name *" required value={form.business_name} onChange={(v) => setForm({...form, business_name: v})}/>
      <Input testid="vendor-signup-contact" label="Your name *" required value={form.contact_name} onChange={(v) => setForm({...form, contact_name: v})}/>
      <Input testid="vendor-signup-email" label="Email *" type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})}/>
      <Input testid="vendor-signup-password" label="Password *" type="password" required value={form.password} onChange={(v) => setForm({...form, password: v})}/>
      <Input testid="vendor-signup-phone" label="Phone" value={form.phone} onChange={(v) => setForm({...form, phone: v})}/>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Category *</label>
        <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
          data-testid="vendor-signup-category"
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <ProvinceMultiSelect value={form.provinces} onChange={(v) => setForm({...form, provinces: v})} testid="vendor-signup-provinces"/>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tell landlords what you do</label>
        <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
          data-testid="vendor-signup-desc" maxLength={1000}
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="vendor-signup-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="vendor-signup-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserPlus className="w-4 h-4"/>} Create account
      </button>
    </form>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
    </div>
  );
}

function ProvinceMultiSelect({ value = [], onChange, testid }) {
  function toggle(p) {
    const has = value.includes(p);
    let next;
    if (p === "ALL") next = has ? [] : ["ALL"];
    else next = has ? value.filter(x => x !== p) : [...value.filter(x => x !== "ALL"), p];
    onChange(next.length ? next : ["ALL"]);
  }
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Service area</label>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
        {PROVINCES.map(p => {
          const on = value.includes(p);
          return (
            <button type="button" key={p} onClick={() => toggle(p)} data-testid={`${testid}-${p}`}
              className={`text-xs font-bold px-2 py-1 rounded-md border ${on ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              {p === "ALL" ? "Whole of Canada" : p}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────── DASHBOARD ────────────────────────────
function Dashboard({ account, vendor: initialVendor, onLogout }) {
  const [tab, setTab] = useState("inbox");
  const [vendor, setVendor] = useState(initialVendor);
  const nav = useNavigate();

  function logout() {
    vendorApi("/vendor-auth/logout", { method: "POST" }).catch(() => { /* ignore */ });
    setToken("");
    onLogout?.();
    nav("/vendor/login", { replace: true });
  }

  return (
    <div className="bg-white min-h-screen" data-testid="vendor-dashboard">
      <PublicHeader light={true}/>
      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Vendor portal</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tighter" data-testid="vendor-dashboard-title">
              {account?.business_name || "Your vendor account"}
            </h1>
            <p className="text-xs text-slate-500 mt-1">{account?.email}</p>
          </div>
          <button onClick={logout} data-testid="vendor-logout"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-xs font-bold text-slate-700">
            <LogOut className="w-3.5 h-3.5"/> Sign out
          </button>
        </div>

        {vendor && !vendor.published && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="vendor-pending-banner">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-none"/>
            <div>Your listing is awaiting admin approval — usually within 1 business day. You can already see incoming inquiries and edit your profile while you wait.</div>
          </div>
        )}

        <FeaturedUpgradeCard vendor={vendor}/>

        <div className="flex gap-1 border-b border-slate-200 mb-6" data-testid="vendor-tabs">
          {[
            {k: "inbox", label: "Inbox", icon: Inbox},
            {k: "profile", label: "Profile", icon: Edit3},
            {k: "reviews", label: "Reviews", icon: Star},
          ].map(({k,label,icon: Icon}) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`vendor-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold border-b-2 transition ${tab===k?"border-emerald-700 text-slate-900":"border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-4 h-4"/> {label}
            </button>
          ))}
        </div>

        {tab === "inbox" && <Inbox_ />}
        {tab === "profile" && vendor && <ProfileEditor vendor={vendor} onSaved={setVendor}/>}
        {tab === "reviews" && <Reviews_/>}
      </main>
    </div>
  );
}

function Inbox_() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all");
  async function load() {
    setBusy(true);
    try {
      const q = filter === "all" ? "" : `?status_filter=${filter}`;
      const d = await vendorApi(`/vendor/inquiries${q}`);
      setData(d);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);
  async function act(id, action) {
    const note = action === "decline" ? window.prompt("Reason (optional, sent to the landlord):") || "" : "";
    try {
      await vendorApi(`/vendor/inquiries/${id}/${action}`, { method: "POST", body: { note } });
      toast.success(`Marked ${action === "accept" ? "accepted" : action === "decline" ? "declined" : "completed"}`);
      load();
    } catch (e) { toast.error(String(e.message || e)); }
  }
  const filters = useMemo(() => ([
    { k: "all", label: "All" },
    { k: "new", label: `New (${data.stats?.new || 0})` },
    { k: "accepted", label: "Accepted" },
    { k: "completed", label: "Completed" },
    { k: "declined", label: "Declined" },
  ]), [data.stats]);

  return (
    <div className="space-y-3" data-testid="vendor-inbox">
      <div className="flex flex-wrap gap-1.5">
        {filters.map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`vendor-inbox-filter-${f.k}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-md border ${filter===f.k?"bg-emerald-600 text-white border-emerald-700":"bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>

      {busy ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto"/></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="vendor-inbox-empty">
          <Inbox className="w-8 h-8 text-slate-400 mx-auto mb-2"/>
          <div className="font-display text-base font-bold text-slate-900">No inquiries here yet</div>
          <p className="text-xs text-slate-500 mt-1">Landlords browsing the marketplace will land in this inbox when they request your services.</p>
        </div>
      ) : (
        data.items.map(iq => <InquiryCard key={iq.id} iq={iq} onAct={act}/>)
      )}
    </div>
  );
}

function InquiryCard({ iq, onAct }) {
  const isOpen = iq.status === "new";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`vendor-inquiry-${iq.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold text-slate-900 truncate" data-testid={`vendor-inquiry-landlord-${iq.id}`}>
            {iq.landlord_name}
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3"/>{iq.landlord_email}</span>
            {iq.landlord_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{iq.landlord_phone}</span>}
            {iq.property_address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{iq.property_address}</span>}
            {iq.service_type && <span className="font-bold text-emerald-700">{iq.service_type}</span>}
          </div>
        </div>
        <StatusPill status={iq.status} testid={`vendor-inquiry-status-${iq.id}`}/>
      </div>
      <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap" data-testid={`vendor-inquiry-message-${iq.id}`}>{iq.message}</p>
      {iq.vendor_note && (
        <div className="mt-2 text-xs text-slate-500 italic">Your note: {iq.vendor_note}</div>
      )}
      <div className="mt-3 text-[10px] text-slate-400">Received {new Date(iq.created_at).toLocaleString()}</div>
      {isOpen && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`vendor-inquiry-actions-${iq.id}`}>
          <button onClick={() => onAct(iq.id, "accept")} data-testid={`vendor-inquiry-accept-${iq.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">
            <CheckCircle2 className="w-3 h-3"/> Accept
          </button>
          <button onClick={() => onAct(iq.id, "decline")} data-testid={`vendor-inquiry-decline-${iq.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-rose-700 text-xs font-bold">
            <XCircle className="w-3 h-3"/> Decline
          </button>
        </div>
      )}
      {iq.status === "accepted" && (
        <button onClick={() => onAct(iq.id, "complete")} data-testid={`vendor-inquiry-complete-${iq.id}`}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold">
          <CheckCircle2 className="w-3 h-3"/> Mark completed
        </button>
      )}
    </div>
  );
}

function StatusPill({ status, testid }) {
  const meta = {
    new:       { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "New" },
    accepted:  { tone: "bg-blue-50 text-blue-700 border-blue-200", label: "Accepted" },
    declined:  { tone: "bg-rose-50 text-rose-700 border-rose-200", label: "Declined" },
    completed: { tone: "bg-slate-100 text-slate-700 border-slate-200", label: "Completed" },
    cancelled: { tone: "bg-slate-50 text-slate-500 border-slate-200", label: "Cancelled" },
  }[status] || { tone: "bg-slate-50 text-slate-500 border-slate-200", label: status };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-wide font-bold border ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function ProfileEditor({ vendor, onSaved }) {
  const [form, setForm] = useState({
    business_name: vendor.name || "",
    description: vendor.description || "",
    phone: vendor.phone || "",
    contact_email: vendor.contact_email || "",
    website: vendor.website || "",
    category: vendor.category || CATEGORIES[0],
    provinces: vendor.provinces || ["ALL"],
    photo_url: vendor.photo_url || "",
  });
  const [busy, setBusy] = useState(false);
  async function save(e) {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const d = await vendorApi("/vendor/profile", { method: "PATCH", body: form });
      onSaved(d.vendor); toast.success("Profile saved");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={save} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 max-w-2xl" data-testid="vendor-profile-form">
      <Input testid="vendor-profile-business" label="Business name *" required value={form.business_name} onChange={(v) => setForm({...form, business_name: v})}/>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">About</label>
        <textarea rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
          data-testid="vendor-profile-desc" maxLength={2000}
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input testid="vendor-profile-phone" label="Phone" value={form.phone} onChange={(v) => setForm({...form, phone: v})}/>
        <Input testid="vendor-profile-email" label="Contact email" type="email" value={form.contact_email} onChange={(v) => setForm({...form, contact_email: v})}/>
      </div>
      <Input testid="vendor-profile-website" label="Website" value={form.website} onChange={(v) => setForm({...form, website: v})}/>
      <Input testid="vendor-profile-photo" label="Photo URL" value={form.photo_url} onChange={(v) => setForm({...form, photo_url: v})}/>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Category</label>
        <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
          data-testid="vendor-profile-category"
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <ProvinceMultiSelect value={form.provinces} onChange={(v) => setForm({...form, provinces: v})} testid="vendor-profile-provinces"/>
      <button type="submit" disabled={busy} data-testid="vendor-profile-save"
        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Edit3 className="w-4 h-4"/>} Save profile
      </button>
    </form>
  );
}

function Reviews_() {
  const [data, setData] = useState({ items: [], count: 0, average: null });
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    vendorApi("/vendor/reviews").then(setData).catch(() => setData({ items: [], count: 0, average: null })).finally(() => setBusy(false));
  }, []);
  if (busy) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 inline"/></div>;
  if (!data.items.length) return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="vendor-reviews-empty">
      <Star className="w-8 h-8 text-slate-400 mx-auto mb-2"/>
      <div className="font-display text-base font-bold text-slate-900">No reviews yet</div>
      <p className="text-xs text-slate-500 mt-1">Once you've completed a few jobs, ask the landlord to leave a review from their dashboard.</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="vendor-reviews-list">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
        <div className="font-display text-4xl font-extrabold text-slate-900 tabular-nums">{data.average?.toFixed?.(1) || "—"}</div>
        <div className="text-xs text-slate-500">{data.count} review{data.count === 1 ? "" : "s"}</div>
      </div>
      {data.items.map(r => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "fill-transparent text-slate-300"}`}/>)}
          </div>
          <p className="mt-2 text-sm text-slate-700">{r.text}</p>
          <div className="mt-2 text-[10px] text-slate-400">{r.landlord_name || "Anonymous"} · {new Date(r.created_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────── FEATURED UPGRADE ────────────────────────────
function FeaturedUpgradeCard({ vendor }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const d = await vendorApi("/monetization/vendor-featured/mine");
      setStatus(d);
    } catch (_) {
      setStatus({ active: false });
    }
  }
  useEffect(() => { load(); }, []);

  // Stripe redirect: ?paid=featured&session_id=…
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const paid = sp.get("paid");
    const sid = sp.get("session_id");
    if (paid === "featured" && sid) {
      vendorApi(`/monetization/vendor-featured/status/${sid}`)
        .then((r) => {
          if (r.activated) toast.success("Featured upgrade activated — pinned for 30 days.");
          // Strip query so a refresh doesn't re-trigger.
          const url = new URL(window.location.href);
          url.searchParams.delete("paid");
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
          load();
        })
        .catch(() => { /* swallow */ });
    } else if (paid === "cancelled") {
      toast.message("Upgrade cancelled — no charge made.");
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  async function upgrade() {
    setBusy(true);
    try {
      const d = await vendorApi("/monetization/vendor-featured/checkout", {
        method: "POST",
        body: { origin: window.location.origin },
      });
      window.location.href = d.url;
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (!status) return null;

  if (status.active) {
    const expires = status.expires_at
      ? new Date(status.expires_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
      : "";
    return (
      <div className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 p-4 mb-4 flex items-center gap-3" data-testid="vendor-featured-active">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-sm">
          <Crown className="w-5 h-5 text-amber-950"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-extrabold text-amber-950">Featured — Active</div>
          <div className="text-xs text-amber-900">
            {vendor?.name || "Your listing"} is pinned to the top of {vendor?.category || "your trade"}
            {expires ? ` · expires ${expires}` : ""}.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4" data-testid="vendor-featured-cta">
      <div className="flex flex-wrap items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
          <Rocket className="w-5 h-5 text-amber-700"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-extrabold text-amber-950">Pin your listing to the top — $49 / 30 days</div>
          <ul className="mt-1 text-xs text-amber-900 space-y-0.5">
            <li>· Top-of-list placement in your trade + provinces</li>
            <li>· &quot;Featured&quot; gold ribbon on every search result</li>
            <li>· Auto-expires after 30 days — no recurring billing</li>
          </ul>
          <button onClick={upgrade} disabled={busy} data-testid="vendor-featured-upgrade"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-xs font-bold disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Crown className="w-3.5 h-3.5"/>} Upgrade to Featured — $49
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────── ROUTE ENTRY ────────────────────────────
export default function VendorPortal({ mode = "auto" }) {
  const [me, setMe] = useState(undefined); // undefined = loading
  useEffect(() => {
    if (!getToken()) { setMe(null); return; }
    vendorApi("/vendor-auth/me").then(setMe).catch(() => { setToken(""); setMe(null); });
  }, []);

  if (me === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-6 h-6 animate-spin text-emerald-600"/></div>;
  }
  if (!me) {
    if (mode === "dashboard") return <Navigate to="/vendor/login" replace/>;
    return <LoginSignup onAuthed={setMe}/>;
  }
  if (mode === "login") return <Navigate to="/vendor/dashboard" replace/>;
  return <Dashboard account={me.account} vendor={me.vendor} onLogout={() => setMe(null)}/>;
}
